/**
 * Observable-like parity for the legacy `EY_RAPToR_URLClientChange` /
 * `EY_RAPToR_URLClientRefreshInTabs` globals
 * (`raptor-shell-header/app.component.ts:72-76`).
 *
 * Legacy returns an RxJS `Observable<any>` that emits once when the URL-driven
 * client switch completes. Callers (notably `base-route.guard`) subscribe and
 * wait before continuing navigation.
 *
 * To avoid pulling RxJS into the React shell we expose an EventTarget-based
 * subject. The SDK shim in M2.9 wraps this in an RxJS-compatible facade for
 * legacy MFE callers.
 */

import { getTracker } from '@/telemetry/tracker';
import { useClientStore } from './client-store';
import { postClientRefresh } from './broadcast';

type Listener = (clientId: string) => void;

const target = new EventTarget();
const EVENT = 'url-client-change';

/**
 * MFE URL-write suppression: Angular MFEs (e.g. PIE) use PathLocationStrategy
 * which calls history.pushState/replaceState to manage their internal routes.
 * These URL writes include the clientId segment (e.g. /{clientId}/app-container/pie/...).
 * TanStack Router patches history methods and treats these as navigations,
 * running beforeLoad → persistLegacyRouteClient. If the MFE writes a stale
 * client (from cached routes or lazy module init), the store flips, the key
 * changes, PIE remounts, and the cycle repeats infinitely.
 *
 * The store is the source of truth for the active client. URL-driven client
 * changes are only allowed when no client is set yet (cold start) or when
 * the change originates from the shell itself (user action, cross-tab sync).
 * MFE-initiated URL writes that reference a different client are silently
 * corrected back to the current client.
 */

export function onUrlClientChange(listener: Listener): () => void {
  const wrapped = (e: Event) => listener((e as CustomEvent<string>).detail);
  target.addEventListener(EVENT, wrapped);
  return () => target.removeEventListener(EVENT, wrapped);
}

/**
 * Trigger a URL-driven client switch. Performs the same sequence as the
 * legacy header `URLClientChange`:
 *   1. Resolve the target client from the available list.
 *   2. Commit it to the store (which persists legacy storage keys).
 *   3. Mark MSAL for forced refresh on the next token acquisition (D9).
 *   4. Post `clientRefresh_channel` to other tabs.
 *   5. Emit the local event for in-shell subscribers.
 *
 * Returns a one-shot Promise that resolves once the switch has been applied
 * locally; the shim converts this to `Observable.of(...)` for legacy callers.
 */
export function triggerUrlClientChange(clientId: string): Promise<string> {
  const state = useClientStore.getState();
  const currentId = state.current?.id ?? null;

  // If the requested client is already active, emit and return (no-op).
  if (clientId === currentId) {
    target.dispatchEvent(new CustomEvent(EVENT, { detail: clientId }));
    return Promise.resolve(clientId);
  }

  // Suppression: if the store already has a client set, an MFE calling
  // EY_RAPToR_URLClientChange with a DIFFERENT client is a stale artefact
  // (e.g. PIE reading an old cached client on bootstrap). The shell's
  // client switch is always initiated via the header modal or cross-tab,
  // which set the store first then navigate. An MFE should never drive
  // the shell's active client.
  if (currentId) {
    target.dispatchEvent(new CustomEvent(EVENT, { detail: currentId }));
    return Promise.resolve(currentId);
  }

  const next = state.available.find((c) => c.id === clientId);
  const previousId = currentId;

  getTracker().event('url_client_change', {
    requestedId: clientId,
    resolved: Boolean(next),
    previousId,
  });

  if (!next) {
    target.dispatchEvent(new CustomEvent(EVENT, { detail: clientId }));
    return Promise.resolve(clientId);
  }

  state.setCurrent(next, { source: 'url-change', silent: true });
  postClientRefresh({ clientId: next.id, previousClientId: previousId, source: 'url-change' });
  target.dispatchEvent(new CustomEvent(EVENT, { detail: next.id }));
  return Promise.resolve(next.id);
}
